import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ExpertFormModal = ({ isOpen, onClose, onSubmit, expert }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    bio: '',
    photo_url: '',
    expertise_areas: '',
    organization: '',
    linkedin_url: '',
    twitter_url: '',
    website_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (expert) {
      setFormData({
        name: expert?.name || '',
        title: expert?.title || '',
        bio: expert?.bio || '',
        photo_url: expert?.photo_url || '',
        expertise_areas: expert?.expertise_areas?.join(', ') || '',
        organization: expert?.organization || '',
        linkedin_url: expert?.linkedin_url || '',
        twitter_url: expert?.twitter_url || '',
        website_url: expert?.website_url || ''
      });
    } else {
      setFormData({
        name: '',
        title: '',
        bio: '',
        photo_url: '',
        expertise_areas: '',
        organization: '',
        linkedin_url: '',
        twitter_url: '',
        website_url: ''
      });
    }
    setError('');
  }, [expert, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.name || !formData?.title) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        expertise_areas: formData?.expertise_areas
          ?.split(',')
          ?.map((area) => area?.trim())
          ?.filter(Boolean)
      };
      await onSubmit(submitData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save expert profile');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {expert ? 'Edit Expert Profile' : 'Create Expert Profile'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Name <span className="text-error">*</span>
              </label>
              <Input
                name="name"
                value={formData?.name}
                onChange={handleChange}
                placeholder="Dr. John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title <span className="text-error">*</span>
              </label>
              <Input
                name="title"
                value={formData?.title}
                onChange={handleChange}
                placeholder="Professor of Computer Science"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData?.bio}
              onChange={handleChange}
              placeholder="Brief biography of the expert"
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Photo URL
            </label>
            <Input
              name="photo_url"
              value={formData?.photo_url}
              onChange={handleChange}
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Expertise Areas (comma-separated)
            </label>
            <Input
              name="expertise_areas"
              value={formData?.expertise_areas}
              onChange={handleChange}
              placeholder="AI, Machine Learning, Data Science"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Organization
            </label>
            <Input
              name="organization"
              value={formData?.organization}
              onChange={handleChange}
              placeholder="MIT, Google, etc."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                LinkedIn URL
              </label>
              <Input
                name="linkedin_url"
                value={formData?.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Twitter URL
              </label>
              <Input
                name="twitter_url"
                value={formData?.twitter_url}
                onChange={handleChange}
                placeholder="https://twitter.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Website URL
              </label>
              <Input
                name="website_url"
                value={formData?.website_url}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Saving...' : expert ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpertFormModal;